import React, { useState, useEffect } from 'react';
import { IDebitmanagment, REPOSSESSION_PRIORITY } from '../../../types/repossession.type';
import { useToastContext } from '../../../common/components/ui/toast/toast.context';
import { getVehicleById } from '../../../common/services/vehicles.api';
import { sendRepossessionDebitManagement } from '../../../common/services/debit-management.api';
import ButtonWithLoader from '../../../common/components/ui/loading/ButtonLoading';

interface NegotiationModalProps {
  initialIsOpen?: boolean;
  onClose: () => void;
  data?: IDebitmanagment;
}

const RED = '#e44131';

const InfoCard = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="border border-gray-300 rounded-lg p-4 bg-white">
    <p className="text-xs font-semibold uppercase tracking-wider mb-1 text-gray-500">{label}</p>
    <div className="font-semibold text-sm text-gray-800">{children}</div>
  </div>
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="text-xs font-semibold uppercase tracking-wider mb-2 text-gray-500">{children}</p>
);

const DebtCollectionModal: React.FC<NegotiationModalProps> = ({
  initialIsOpen = false,
  onClose,
  data
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(initialIsOpen);
  const [debitmanagmentItem, setDebitmanagmentItem] = useState<IDebitmanagment | null>(null);
  const [namePartner, setNamePartner] = useState<string | null>(null);
  const [isLoadingRepossession, setIsLoadingRepossession] = useState<boolean>(false);
  const [observation, setObservation] = useState<string>('');
  const [priority, setPriority] = useState<REPOSSESSION_PRIORITY>(REPOSSESSION_PRIORITY.MEDIUM);

  const { notifySuccess, notifyError } = useToastContext();

  const handleSendRepossession = async () => {
    try {
      setIsLoadingRepossession(true);

      const result = await sendRepossessionDebitManagement(
        debitmanagmentItem?.renter?.vehicleId || '',
        debitmanagmentItem?.daysOverdue || 0,
        Number(debitmanagmentItem?.amount) || 0,
        {
          _id: debitmanagmentItem?.renter?.id || '',
          name: debitmanagmentItem?.renter?.name || '',
          whatsapp: debitmanagmentItem?.renter?.whatsapp || '',
        },
        observation,
        priority,
      );

      if (!result.ok) {
        return notifyError(result.message || 'Erro ao enviar para recolhimento');
      }

      setObservation('');
      setPriority(REPOSSESSION_PRIORITY.MEDIUM);
      handleClose();
      return notifySuccess('Recolhimento enviado com sucesso');
    } catch (error) {
      notifyError('Erro ao enviar para recolhimento');
    } finally {
      setIsLoadingRepossession(false);
    }
  };

  const fetchData = async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { _original } = data as any || {};
    const result = _original as IDebitmanagment;
    setIsOpen(initialIsOpen);
    setDebitmanagmentItem(result || null);
    setObservation('');
    setPriority(REPOSSESSION_PRIORITY.MEDIUM);

    if (result?.renter?.partnerId) {
      try {
        const resultVehicle = await getVehicleById(result.renter.vehicleId || '');
        if (!resultVehicle.ok) {
          setNamePartner(null);
          return notifyError(resultVehicle.message || 'Erro ao buscar veículo');
        }
        setNamePartner(resultVehicle.data?.partnerInfo.companyName || null);
      } catch (error) {
        setNamePartner(null);
        notifyError('Erro ao buscar veículo');
      }
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIsOpen, data, notifyError]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    if (isOpen) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  if (!isOpen) return null;

  const isPartnerVehicle = debitmanagmentItem?.renter?.partnerId && !namePartner?.toUpperCase().includes('LOCAVIBE');

  const formattedAmount = debitmanagmentItem?.amount
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(debitmanagmentItem.amount))
    : 'N/A';

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-h-[92vh] flex flex-col overflow-hidden"
        style={{ maxWidth: '80%' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-300">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${RED}22` }}>
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" style={{ color: RED }} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-base leading-tight text-gray-900">Enviar para Recolhimento</h2>
            <p className="text-xs mt-0.5 truncate text-gray-500">{debitmanagmentItem?.renter?.name || ''}</p>
          </div>
          <button
            onClick={handleClose}
            aria-label="Fechar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-5 space-y-5 bg-gray-100">

          {/* Cliente */}
          <div>
            <SectionLabel>Cliente</SectionLabel>
            <div className="rounded-lg overflow-hidden border border-gray-300 bg-white">
              {[
                { label: 'Nome', value: debitmanagmentItem?.renter?.name || 'N/A' },
                { label: 'CPF', value: debitmanagmentItem?.renter?.cpf || 'N/A' },
                {
                  label: 'WhatsApp',
                  value: debitmanagmentItem?.renter?.whatsapp
                    ? <a href={`https://wa.me/55${String(debitmanagmentItem.renter.whatsapp).replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ color: RED }}>{debitmanagmentItem.renter.whatsapp}</a>
                    : 'N/A',
                },
              ].map((row, idx) => (
                <div key={idx} className={`flex items-center px-4 py-2.5 text-sm ${idx % 2 !== 0 ? 'bg-gray-50' : ''}`}>
                  <span className="w-1/4 font-medium text-gray-500">{row.label}</span>
                  <span className="text-gray-800">{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-2 gap-3">
            <InfoCard label="Valor em Aberto">
              <span style={{ color: RED }}>{formattedAmount}</span>
            </InfoCard>
            <InfoCard label="Atraso">
              <span className="text-orange-500">{debitmanagmentItem?.daysOverdue ?? 'N/A'} dias</span>
            </InfoCard>
            <InfoCard label="Veículo de Parceiro">
              {isPartnerVehicle
                ? <span style={{ color: RED }}>Sim</span>
                : <span className="text-gray-800">Não</span>
              }
            </InfoCard>
            <InfoCard label="Proprietário">
              <span className="text-gray-800">{namePartner || 'N/A'}</span>
            </InfoCard>
          </div>

          {/* Prioridade */}
          <div>
            <SectionLabel>Prioridade</SectionLabel>
            <div className="flex gap-2">
              {([
                { value: REPOSSESSION_PRIORITY.HIGH, label: 'Alta' },
                { value: REPOSSESSION_PRIORITY.MEDIUM, label: 'Média' },
                { value: REPOSSESSION_PRIORITY.LOW, label: 'Baixa' },
              ] as const).map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setPriority(value)}
                  className="flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors"
                  style={priority === value
                    ? { backgroundColor: RED, color: '#fff', borderColor: RED }
                    : { backgroundColor: '#fff', color: '#374151', borderColor: '#d1d5db' }
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Observação */}
          <div>
            <SectionLabel>Observação</SectionLabel>
            <textarea
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              rows={4}
              placeholder="Adicione uma observação para a equipe de recolhimento..."
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none resize-none bg-white"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-300 bg-white">
          <ButtonWithLoader
            type="button"
            isLoading={isLoadingRepossession}
            onClick={handleSendRepossession}
          >
            Enviar para recolhimento
          </ButtonWithLoader>
        </div>
      </div>
    </div>
  );
};

export default DebtCollectionModal;
